import { useForm } from 'react-hook-form';
import { FormTextField } from 'src/components/Forms/FormTextField/form-text-field';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalStep, useModalStep } from 'src/components/ModalContainer';

enum SaveProfileFormField {
  Name = 'name',
}

interface SaveProfileFormValues {
  [SaveProfileFormField.Name]: string;
}

const defaultValues: SaveProfileFormValues = {
  [SaveProfileFormField.Name]: '',
};

const NAME_REQUIRED_MESSAGE = 'Profile name is required';

interface SaveProfileStepProps {
  existingNames: string[];
}

/** Pops the trimmed name of the new profile, or nothing when cancelled. */
export const SaveProfileStep = ({ existingNames }: SaveProfileStepProps) => {
  const { popStep } = useModalStep<string>();
  const formMethods = useForm<SaveProfileFormValues>({ defaultValues, mode: 'onChange' });
  const {
    formState: { isValid },
    handleSubmit,
  } = formMethods;

  const validateName = (value: string) => {
    const trimmed = value.trim();
    if (trimmed === '') {
      return NAME_REQUIRED_MESSAGE;
    }
    const isDuplicate = existingNames.some(
      (existing) => existing.toLowerCase() === trimmed.toLowerCase(),
    );
    return isDuplicate ? 'You already have a profile with this name' : true;
  };

  const submit = handleSubmit((values) => popStep(values[SaveProfileFormField.Name].trim()));

  return (
    <ModalStep
      title="Save current as profile"
      testId="save-profile-dialog"
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'save-profile-cancel' },
        {
          label: 'Save profile',
          kind: 'primary',
          onClick: () => void submit(),
          disabled: !isValid,
          testId: 'save-profile-submit',
        },
      ]}
    >
      <GenericForm<SaveProfileFormValues> formMethods={formMethods}>
        <FormTextField
          type="textfield"
          name={SaveProfileFormField.Name}
          label="Profile name"
          fullWidth
          required={NAME_REQUIRED_MESSAGE}
          rules={{ validate: validateName }}
          showErrorText
          textFieldProps={{ inputProps: { 'data-testid': 'save-profile-name' } }}
        />
      </GenericForm>
    </ModalStep>
  );
};
